import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    input,
    OnDestroy,
    Optional,
    Output,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { fromEvent, Observable, of } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeWhile } from 'rxjs/operators';

import {
    blurAutocompleter,
    decrementSelectedIndex,
    focusAutocompleter,
    incrementSelectedIndex,
    selectResultByIndex,
    setAutocompleteCategoriesResults,
    setAutocompleteInputText,
    setAutocompleteListResults,
    setAutocompleteShouldBlur,
} from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.actions';
import { AUTOCOMPLETER_STATE_SLICE_KEY } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.reducer';

import {
    ADD_BUTTON,
    ICON_BROWSE,
    ICON_SEARCH,
    NO_ICON_SEARCH,
} from 'SearchApp/ts/features/base-autocomplete/base-autocomplete.constants';
import { AutocompleteSearchButtonType } from 'SearchApp/ts/features/base-autocomplete/base-autocomplete.types';
import { AutocompleteCategory } from 'SearchApp/ts/types/autocomplete-category.type';
import {
    AutocompleteExtraLink,
    CategoriesAutocompleteResults,
} from 'SearchApp/ts/types/categories-autocomplete-results.type';
import { AutocompleteStore } from 'SearchApp/ts/features/base-autocomplete/state/interfaces/autocomplete-store.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { SearchService } from 'SearchApp/ts/services/search.service';

import { FIGURE } from 'Angular/utils/number_formatter';
import { getReversedFigure } from 'Angular/utils/number_formatter_reverse';

import { ADDITIONAL_AUTOCOMPLETER_QUERY_PARAMS } from 'Angular/superclasses/autocompleter-additional-query-params';

/**
 *
 * Search Bar Component
 *
 * This component is responsible for reading user input being typed into the search bar, and using it to make backend
 * requests. Once it receives data from the backend, it updates the NgRx store.
 *
 * It also handles special actions, and dispatches NgRx actions accordingly:
 *   1. Key presses (DOWN, UP, ENTER, ESC)
 *   2. Focusing the search bar
 *   3. Blurring the search bar
 *   4. Hovering / clicking the search icon
 *
 * Input Properties:
 *
 *   autocompleterId: The UUID that was created by the parent component to manage NgRx state.
 *   buttonType: 'add', 'icon-search', 'icon-browse'; determines which 'icon' to show to the user.
 *   categoriesExtraLinks: Extra links to append to categories result.
 *   buttonText: Text used for the search bar.
 *   includeSearchIcon: Whether the magnifying glass search icon is included in the search bar
 *   inputAutocomplete: The input's autocomplete attribute; see BaseAutocompleteComponent.
 *   isDisabled: Boolean that disables input tag.
 *   isInvalid: Boolean that shows invalid css in search bar.
 *   placeholder: placeholder text to show when the input box is empty.
 *   searchType: Which autocompleter results should be fetched from.
 *   searchBlockRegex: Enable raw inputs that match the regex.
 *   searchableSecurityIds: Security IDs that are searchable.
 *   resultsType: returned type of list or categories dict.
 *   defaultOnly: bool needed for Data Independence to know if we are on RB or Quote page AND that we are set the Default View
 *
 * Output Properties:
 *
 *   searchBlockHandler: EventEmitter that emits the input when searchBlockRegex is set and matches.
 *   backspaceHandler: EventEmitter that emits when backspace is pressed.
 *   browseClickHandler:  EventEmitter that emits the value passed to it by the search bar's button click handler.
 *   searchBarBlurHandler: EventEmitter that emits the input when searchBar blur event occurs.
 */

@Component({ standalone: false, selector: 'ycn-search-bar', templateUrl: './search-bar.template.html' })
export class SearchBarComponent implements AfterViewInit, OnDestroy {
    @Input() autocompleterId!: string;

    @Input() buttonType!: AutocompleteSearchButtonType;

    @Input() categoriesExtraLinks: AutocompleteExtraLink[] = [];

    @Input() includeSearchIcon: boolean = false;

    readonly inputAutocomplete = input<string>('off');

    @Input() buttonText: string = '';

    @Input() placeholder!: string;

    @Input() searchType!: string;

    @Input() searchBlockRegex?: string;

    @Input() isDisabled = false;

    @Input() isInvalid: boolean = false;

    @Input() hideButton: boolean = false;

    @Input() resultsType: 'list' | 'categories' = 'list';

    @Input() searchableSecurityIds: string[] = [];

    @Input() defaultOnly: boolean = false;

    @Output() searchBlockHandler: EventEmitter<string> = new EventEmitter<string>();

    @Output() backspaceHandler: EventEmitter<void> = new EventEmitter<void>();

    @Output() browseClickHandler: EventEmitter<void> = new EventEmitter<void>();

    @Output() searchBarBlurHandler = new EventEmitter<string>();

    @ViewChild('autocompleteInput') textInput!: ElementRef;

    readonly AUTOCOMPLETE_SEARCH_BUTTON_TYPES: { [k: string]: AutocompleteSearchButtonType } = {
        ADD_BUTTON,
        ICON_BROWSE,
        ICON_SEARCH,
        NO_ICON_SEARCH,
    };

    cacheData: { [k: string]: AutocompleteResult[] | CategoriesAutocompleteResults } = {}; // maps a cacheKey to its associated results

    cacheKey: string = '';

    shouldBlur: boolean = false;

    showResults: boolean = false;

    isAlive: boolean = true;

    // The input text in the store when it last changed
    private storedInputText: string = '';

    // The results returned from the backend are stored here
    results: AutocompleteResult[] = [];

    MULTI_ITEM_SEARCH_TYPES = ['multi_asset_watchlist_securities', 'indicator'];

    constructor(
        private searchService: SearchService,
        protected readonly store: Store<{ autocompleter: AutocompleteStore }>,
        @Optional()
        @Inject(ADDITIONAL_AUTOCOMPLETER_QUERY_PARAMS)
        private readonly additionalAutoCompleterQueryParams?: Record<string, any>
    ) {
        this.store
            .select(AUTOCOMPLETER_STATE_SLICE_KEY)
            .pipe(takeWhile(() => this.isAlive))
            .subscribe(this.mapStateToComponent);
    }

    mapStateToComponent = (state: AutocompleteStore): void => {
        const autocompleterState = state[this.autocompleterId];

        if (!autocompleterState) {
            return;
        }
        this.showResults = autocompleterState.showResults || autocompleterState.extraLinks.length > 0;
        this.shouldBlur = autocompleterState.shouldBlur;

        // Show stored text that changed, so text set from outside (e.g. BaseAutocompleteComponent.setInputText) appears.
        // Typed text is stored as it is in the input, so this doesn't overwrite typing.
        const { inputText } = autocompleterState;
        if (this.textInput && (inputText === '' || inputText !== this.storedInputText)) {
            this.textInput.nativeElement.value = inputText;
        }
        this.storedInputText = inputText;
    };

    ngOnDestroy(): void {
        this.isAlive = false;
    }

    ngAfterViewInit(): void {
        // Subscribe to changes in the input. Whenever a new value is emitted, call `getData`
        const typeAhead = fromEvent<KeyboardEvent>(this.textInput.nativeElement, 'input').pipe(
            map((e: KeyboardEvent) => (e.target as HTMLInputElement).value),
            debounceTime(200),
            // Skip terms the input no longer shows, e.g. when a result was selected before the debounce ended
            filter((searchTerm: string) => searchTerm === this.textInput.nativeElement.value),
            switchMap((searchTerm: string) => this.getData(searchTerm))
        );

        typeAhead
            .pipe(takeWhile(() => this.isAlive))
            .subscribe((data: AutocompleteResult[] | CategoriesAutocompleteResults) => {
                this.cacheData[this.cacheKey] = data;

                if (this.resultsType === 'list' && Array.isArray(data)) {
                    const filteredData = this.searchableSecurityIds.length
                        ? this.filterBySearchableSecurityIds(data)
                        : data;
                    this.store.dispatch(
                        setAutocompleteListResults({
                            autocompleterId: this.autocompleterId,
                            results: filteredData,
                        })
                    );
                } else {
                    const filteredData: CategoriesAutocompleteResults = {};
                    Object.entries(data).forEach(([key, listData]) => {
                        const filteredListData = this.searchableSecurityIds.length
                            ? this.filterBySearchableSecurityIds(listData)
                            : listData;
                        if (filteredListData.length > 0) {
                            filteredData[key as AutocompleteCategory] = filteredListData;
                        }
                    });
                    this.store.dispatch(
                        setAutocompleteCategoriesResults({
                            autocompleterId: this.autocompleterId,
                            results: filteredData,
                            extraLinks: this.categoriesExtraLinks,
                        })
                    );
                }
            });
    }

    filterBySearchableSecurityIds(autocompleteResults: AutocompleteResult[]): AutocompleteResult[] {
        return autocompleteResults.filter((result) => {
            return this.searchableSecurityIds.includes(result.search_id);
        });
    }

    getData(inputText: string): Observable<AutocompleteResult[] | CategoriesAutocompleteResults> {
        /**
         * Given a text query, return the result contained in the cache, or make a request to the backend.
         */
        this.store.dispatch(
            setAutocompleteInputText({ autocompleterId: this.autocompleterId, inputText, isFetching: true })
        );

        // If searchBlockRegex is set and matches, call searchBlockHandler and if inputText is a number,
        // return one-result list
        if (
            this.searchBlockRegex &&
            inputText.length > 0 &&
            inputText.match(this.searchBlockRegex) !== null &&
            // @ts-ignore
            inputText.match(this.searchBlockRegex)[0] === inputText
        ) {
            if (this.searchBlockHandler) {
                this.searchBlockHandler.emit(inputText);
            }
            // If the input has a figure format (E.g. 100k), reverse it to get the number.
            const reversedInputText: boolean | number = getReversedFigure(FIGURE, inputText);
            // `reversedInputText` will be false if it has a wrong number format. Don't do anything if that's the case.
            if (reversedInputText !== false) {
                return of([
                    {
                        name: reversedInputText.toString(),
                        is_raw: true,
                        search_id: reversedInputText.toString(),
                    } as AutocompleteResult,
                ]);
            }
            return of([]);
        }

        // Prevent suggest requests for long strings of multiple items. This can result in long request headers
        // that are blocked by our CDN
        if (this.MULTI_ITEM_SEARCH_TYPES.includes(this.searchType) && inputText.length > 200) {
            return of([]);
        }

        this.cacheKey = `${this.searchType}${inputText}${this.defaultOnly}`;
        const cachedResults = this.cacheData[this.cacheKey];
        if (cachedResults) {
            return of(cachedResults);
        }
        return this.searchService.getSuggestDataListResults(
            this.searchType,
            { q: inputText, ...(this.additionalAutoCompleterQueryParams ?? {}) },
            this.defaultOnly
        );
    }

    handleFocus(): void {
        this.store.dispatch(focusAutocompleter({ autocompleterId: this.autocompleterId }));
    }

    handleBlur(): void {
        if (this.shouldBlur) {
            this.searchBarBlurHandler.emit(this.textInput.nativeElement.value);
            this.store.dispatch(blurAutocompleter({ autocompleterId: this.autocompleterId }));
        }
    }

    handleClick(): void {
        if (this.isDisabled) return;
        switch (this.buttonType) {
            case ADD_BUTTON:
            case ICON_SEARCH:
                this.store.dispatch(selectResultByIndex({ autocompleterId: this.autocompleterId }));
                break;
            case ICON_BROWSE:
                this.browseClickHandler.emit();
                this.setShouldBlur(true);
                this.handleBlur();
                break;
            default:
                break;
        }
    }

    setShouldBlur(shouldBlur: boolean): void {
        if (this.isDisabled) return;
        this.store.dispatch(setAutocompleteShouldBlur({ autocompleterId: this.autocompleterId, shouldBlur }));
    }

    // ------------------------------------ Begin KeyDownHelper Callback Methods ------------------------------------
    nextItem(event: Event | KeyboardEvent): void {
        // Select next item if active, otherwise pass this keypress through
        if (this.showResults) {
            this.store.dispatch(incrementSelectedIndex({ autocompleterId: this.autocompleterId }));
            event.preventDefault();
        }
    }

    prevItem(event: Event | KeyboardEvent): void {
        // Select previous item if showResults, otherwise pass this keypress through
        if (this.showResults) {
            this.store.dispatch(decrementSelectedIndex({ autocompleterId: this.autocompleterId }));
            event.preventDefault();
        }
    }

    escPress(): void {
        this.setShouldBlur(true);
        this.textInput.nativeElement.blur();
    }

    enterPress(): void {
        this.store.dispatch(selectResultByIndex({ autocompleterId: this.autocompleterId }));
    }

    backspacePress(): void {
        if (this.backspaceHandler && this.textInput.nativeElement.value === '') {
            this.backspaceHandler.emit();
        }
    }
}
